import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { raygunHttpError } from '@approot/app.raygun.setup';
import {
  AskBidMidNumber,
  IForwardPointsApiRequest,
  KeyValue
} from '@approot/models';
import {
  AlertsService,
  GetForwardPointsPayload,
  LegService
} from '@approot/services';
import { apiURL, reformatDate, shortUuid } from '@approot/utilities';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService, getHeaders } from '../auth/auth.service';
import { PhoenixEventBusService } from '../phoenix/phoenix-event-bus.service';
import { TimingMeta, TimingService } from '../timing.service';

export interface SpotResponse {
  spot: {
    ask: number;
    bid: number;
    mid: number;
    spread: number;
  };
  timestamp: string;
  displayedSpotDate: string;
  spotDate: string;
}

export interface StrikePayload {
  task: KeyValue;
  isDeltaRequired?: boolean;
  timestamp?: string;
}

export interface StrikeResponse {
  strike: number;
  timestamp: string;
  deltaType?: string;
  delta?: number;
  premiumAdjusted?: boolean;
  strikeVol?: AskBidMidNumber;
  forwardPoints?: AskBidMidNumber;
  spot?: AskBidMidNumber;
}

export interface SpotRequest {
  cross: string;
  timestamp?: string;
}

interface SpotCalculatorApiResponse {
  response: {
    spot: SpotResponse;
    timestamp: string;
    meta: TimingMeta;
  };
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class MarketDataService {
  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private alertsService: AlertsService,
    private phxBus: PhoenixEventBusService,
    private legService: LegService,
    private timingService: TimingService
  ) {}

  public calculateSpot(
    payload: SpotRequest
  ): Observable<SpotResponse | unknown> {
    const startTime = Date.now();
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post<SpotCalculatorApiResponse>(
            apiURL('/fx/spot'),
            payload,
            getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            map(r => r.response),
            tap(response =>
              this.timingService.logTimings(response.meta, startTime)
            ),
            catchError(e => this.handleError(e, 'spot', payload, '/fx/spot'))
          );
      })
    );
  }

  public calculateStrike(
    payload: StrikePayload
  ): Observable<StrikeResponse | unknown> {
    return this.authService.currentSession$().pipe(
      switchMap(cognitoToken => {
        return this.http
          .post<StrikeResponse>(
            apiURL('/fx/strike'),
            payload,
            getHeaders(cognitoToken.getIdToken().getJwtToken())
          )
          .pipe(
            catchError(e =>
              this.handleError(e, 'strike', payload, '/fx/strike')
            )
          );
      })
    );
  }

  public getForwardPoints(
    expiryDates: string[],
    currencyPair: string,
    getMarketDataTimeStamp?: string
  ): IForwardPointsApiRequest {
    let payload: GetForwardPointsPayload = {
      task: 'getForwardPoints',
      expirationDates: expiryDates.map(date =>
        reformatDate(date, 'YYYY-MM-DD')
      ),
      currencyPair,
      timestamp: getMarketDataTimeStamp
    };
    return this.legService.marketDataService(payload);
  }

  private handleError(
    err: HttpErrorResponse,
    dataRequested: 'strike' | 'spot',
    payload: any,
    urlPath: string
  ): Observable<never> {
    let message: string;
    if (err.error instanceof ProgressEvent) {
      message =
        'Request has timed out and has been reported.  Please contact your sales representative if needed';
    } else {
      message =
        err.error?.message ||
        err.message ||
        `Unable to load requested ${dataRequested}`;
    }
    raygunHttpError(err, [dataRequested]);
    const id = shortUuid();
    this.phxBus.addEvent({
      errorId: id,
      type: 'hydra',
      category: 'error',
      urlPath,
      payload,
      error: err,
      context: {
        source: 'marketDataService'
      }
    });
    this.alertsService.addAlert({
      message,
      id
    });
    return throwError(() => new Error(`${dataRequested} error`));
  }
}
